import { and, eq, gt, gte, ilike, inArray, like, lt, lte, ne, notInArray, or, type SQL } from "drizzle-orm";
import type { AnyPgColumn } from "drizzle-orm/pg-core";

/** 재귀 필터 최대 깊이 — DoS 방어 (깊이 초과 시 조건 묵살) */
const MAX_FILTER_DEPTH = 5;

/** 지원 연산자 목록 */
const FILTER_OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "in", "nin", "like", "ilike"] as const;
type FilterOp = (typeof FILTER_OPS)[number];

function isValidFilterOp(op: string): op is FilterOp {
  return (FILTER_OPS as readonly string[]).includes(op);
}

export type ParseCrudFilterOptions = {
  /** true면 문자열 패턴 불일치/ilike 연산금지 시 오류 */
  disableILike?: boolean;
  /** true면 금지된 연산자·타입 불일치 시 에러(createCrud) — 기존 crud 레이어 무시 동작 유지 위해 기본은 false 느낌의 미설정만 사용 */
  strictColumnOps?: boolean;
};

/**
 * 필터 노드 트리에 등장하는 컬럼 키(OR·AND 외 모든 키)를 순서대로 수집합니다.
 */
export function extractStructuredFilterColumnKeys(node: Record<string, unknown>, depth = 0): string[] {
  if (depth > MAX_FILTER_DEPTH) return [];
  const keys: string[] = [];
  for (const [k, val] of Object.entries(node)) {
    if (k === "OR" || k === "AND") {
      if (!Array.isArray(val)) continue;
      for (const child of val) {
        if (child != null && typeof child === "object" && !Array.isArray(child)) {
          keys.push(...extractStructuredFilterColumnKeys(child as Record<string, unknown>, depth + 1));
        }
      }
      continue;
    }
    keys.push(k);
  }
  return keys;
}

/**
 * 허용되지 않은 컬럼이 있으면 Error — allowedFilters 미지정이면 무검증(테이블에 있는 컬럼만 후속 단계에서 해석).
 */
export function assertStructuredFiltersAllowlist(
  filters: Record<string, unknown>,
  allowedFilters: readonly string[] | undefined,
): void {
  if (allowedFilters === undefined) return;
  const allow = new Set(allowedFilters);
  const seen = extractStructuredFilterColumnKeys(filters);
  if (seen.length > 0 && allow.size === 0) {
    throw new Error("Structured filters require allowedFilters entries; whitelist is empty");
  }
  for (const k of seen) {
    if (!allow.has(k)) {
      throw new Error(`Filter references disallowed column "${k}"`);
    }
  }
}

function columnAllowsOp(column: AnyPgColumn, op: FilterOp, disableILike: boolean): boolean {
  if (disableILike && op === "ilike") return false;
  const dataType = (column as { dataType?: string }).dataType;

  const noPattern = (): boolean => op !== "like" && op !== "ilike";

  switch (dataType) {
    case "string":
      return true;
    case "number":
    case "bigint":
      return noPattern();
    case "boolean":
      return op === "eq" || op === "ne" || op === "in" || op === "nin";
    case "date":
      return noPattern();
    case "json":
    case "array":
      return op === "eq" || op === "ne" || op === "in" || op === "nin";
    default:
      if (op === "like" || op === "ilike") return true;
      return true;
  }
}

/**
 * 단일 필터 연산자를 Drizzle SQL 조건으로 변환.
 * 지원하지 않는 op이거나 값 타입이 맞지 않으면 undefined 반환 (묵살).
 */
export function applyFilterOp(col: AnyPgColumn, op: FilterOp, value: unknown): SQL | undefined {
  switch (op) {
    case "eq":
      return eq(col, value);
    case "ne":
      return ne(col, value);
    case "gt":
      return gt(col, value as any);
    case "gte":
      return gte(col, value as any);
    case "lt":
      return lt(col, value as any);
    case "lte":
      return lte(col, value as any);
    case "in":
      if (!Array.isArray(value) || value.length === 0) return undefined;
      return inArray(col, value);
    case "nin":
      if (!Array.isArray(value) || value.length === 0) return undefined;
      return notInArray(col, value);
    case "like":
      if (typeof value !== "string") return undefined;
      return like(col, value);
    case "ilike":
      if (typeof value !== "string") return undefined;
      return ilike(col, value);
    default:
      return undefined;
  }
}

/**
 * 재귀적 필터 노드 파싱 — OR/AND 논리 중첩 + 연산자 매핑.
 *
 * @param node         - 필터 JSON 객체
 * @param table        - Drizzle PgTable (컬럼 매핑)
 * @param allowedFields - 허용 필드 화이트리스트 (미지정 시 모든 테이블 컬럼 허용)
 *
 * @example
 * ```ts
 * // 암묵적 eq (v2 하위호환)
 * parseFilterNode({ status: 'active' }, table)
 *   → and(eq(table.status, 'active'))
 *
 * // 명시적 연산자
 * parseFilterNode({ qty: { op: 'gte', value: 10 } }, table)
 *   → and(gte(table.qty, 10))
 *
 * // OR / AND 중첩
 * parseFilterNode({ OR: [{ a: 1 }, { b: { op: 'gt', value: 5 } }] }, table)
 *   → or(eq(table.a, 1), gt(table.b, 5))
 * ```
 */
export function parseFilterNode(
  node: Record<string, unknown>,
  table: any,
  allowedFields?: readonly string[],
  depth: number = 0,
  opts?: ParseCrudFilterOptions,
): SQL | undefined {
  const strict = opts?.strictColumnOps === true;
  const disableILike = opts?.disableILike === true;

  // DoS 방어: 재귀 깊이 제한 초과 시 조건 묵살
  if (depth > MAX_FILTER_DEPTH) return undefined;

  const conditions: SQL[] = [];

  for (const [key, val] of Object.entries(node)) {
    // ── OR 논리 그룹 ──
    if (key === "OR") {
      if (!Array.isArray(val)) continue; // 잘못된 타입 → 묵살
      const orConds = val
        .filter(
          (child): child is Record<string, unknown> =>
            child != null && typeof child === "object" && !Array.isArray(child),
        )
        .map((child) => parseFilterNode(child, table, allowedFields, depth + 1, opts))
        .filter((c): c is SQL => c !== undefined);
      if (orConds.length > 0) conditions.push(or(...orConds)!);
      continue;
    }

    // ── AND 논리 그룹 ──
    if (key === "AND") {
      if (!Array.isArray(val)) continue;
      const andConds = val
        .filter(
          (child): child is Record<string, unknown> =>
            child != null && typeof child === "object" && !Array.isArray(child),
        )
        .map((child) => parseFilterNode(child, table, allowedFields, depth + 1, opts))
        .filter((c): c is SQL => c !== undefined);
      if (andConds.length > 0) conditions.push(and(...andConds)!);
      continue;
    }

    // ── 컬럼 필터 ──
    // allowedFields 화이트리스트 검증 (재귀적으로 OR/AND 내부에도 적용됨)
    if (allowedFields && !allowedFields.includes(key)) {
      if (!strict) continue;
      throw new Error(`Filter references unknown column "${key}"`);
    }

    const col = table[key] as AnyPgColumn | undefined;
    if (!col) {
      if (!strict) continue;
      throw new Error(`Unknown filter column "${key}" on table`);
    }

    // 명시적 연산자: { op: 'gte', value: 10 }
    if (val != null && typeof val === "object" && !Array.isArray(val) && "op" in val) {
      const { op, value } = val as { op: string; value: unknown };
      if (!isValidFilterOp(op)) {
        if (strict) throw new Error(`Unsupported filter operator "${op}"`);
        continue;
      }
      if (!columnAllowsOp(col, op, disableILike)) {
        if (strict) {
          throw new Error(`Filter operator "${op}" is not allowed for column "${key}"`);
        }
        continue;
      }
      const cond = applyFilterOp(col, op, value);
      if (cond) conditions.push(cond);
      else if (strict) throw new Error(`Invalid filter values for "${key}"`);
      continue;
    }

    // 암묵적 eq (v2 하위호환): { status: 'active' } → eq(status, 'active')
    if (!columnAllowsOp(col, "eq", disableILike)) {
      if (strict) throw new Error(`Implicit eq is not allowed for column "${key}"`);
      continue;
    }
    conditions.push(eq(col, val));
  }

  return conditions.length > 0 ? and(...conditions) : undefined;
}
